import React, { FunctionComponent } from 'react';
import {
    getTicketForImage,
    getTicketImageUrls,
    Ticket,
    TicketVariant,
} from '../../../models/tickets';
import { UrgencySignals } from '../../../models/urgencySignals';
import TicketImageThumbnail from '../../ticketImageThumbnail/TicketImageThumbnail';
import '../ticketCardContent.scss';
import TicketContent, { TicketContentProps } from '../TicketContent';
import { getDonationChangeActionPayload } from '../utils';

export interface TicketDisplayCardContentProps
    extends Pick<
        TicketContentProps,
        | 'category'
        | 'cost'
        | 'currencyFormat'
        | 'currencySymbol'
        | 'description'
        | 'displayPrice'
        | 'donationAmount'
        | 'feeAndTax'
        | 'hideSaleDates'
        | 'hideSelect'
        | 'id'
        | 'includeFee'
        | 'includeTax'
        | 'isCurrencySuffix'
        | 'isFormSubmitting'
        | 'isTicketTiered'
        | 'name'
        | 'onJoinWaitlist'
        | 'paymentConstraints'
        | 'primaryPrice'
        | 'quantityRemaining'
        | 'salesEnd'
        | 'salesStart'
        | 'selectedQuantity'
        | 'shouldHideStatus'
        | 'showJoinWaitlist'
        | 'statusKey'
        | 'ticketOptionsRange'
        | 'ticketReseller'
        | 'variantId'
    > {
    image: Ticket['image'];
    onDonationChange: (payload: {
        amount: number;
        id: string;
        variantId?: string;
    }) => void;
    onQuantityChange: (payload: {
        id: string;
        quantity: number;
        variantId?: string;
    }) => void;
    showDiscountedPill: boolean;
    showUnlockedPill: boolean;
    urgencySignals: UrgencySignals | null;
    variants: Array<TicketVariant>;
    isBOGOTicket: boolean;
}

export const TicketDisplayCardContent: FunctionComponent<
    TicketDisplayCardContentProps
> = ({
    category,
    cost,
    currencyFormat,
    currencySymbol,
    description,
    displayPrice,
    donationAmount,
    feeAndTax,
    hideSaleDates,
    hideSelect,
    id,
    image,
    includeFee,
    includeTax,
    isBOGOTicket,
    isCurrencySuffix,
    isFormSubmitting,
    isTicketTiered,
    name,
    onDonationChange,
    onJoinWaitlist,
    onQuantityChange,
    paymentConstraints,
    primaryPrice,
    quantityRemaining,
    salesEnd,
    salesStart,
    selectedQuantity,
    shouldHideStatus,
    showDiscountedPill,
    showJoinWaitlist,
    showUnlockedPill,
    statusKey,
    ticketOptionsRange,
    ticketReseller,
    urgencySignals,
    variantId,
    variants,
}) => {
    const handleQuantityChange = (
        variantId: string | null,
        quantity: string,
    ) => {
        const basePayload = {
            id,
            quantity: parseInt(quantity, 10),
        };

        const payload = variantId
            ? {
                  ...basePayload,
                  variantId,
              }
            : basePayload;

        onQuantityChange?.(payload);
    };

    const handleDonationChange = (amount: string) => {
        onDonationChange?.(
            getDonationChangeActionPayload(id, amount, currencyFormat),
        );
    };

    const ticketForImage = getTicketForImage({
        image,
        variants,
    }) as Pick<Ticket, 'image'> & { name: string };
    const ticketGrahic = ticketForImage ? (
        <TicketImageThumbnail
            title={ticketForImage.name}
            {...getTicketImageUrls(ticketForImage)}
        />
    ) : null;

    return (
        <TicketContent
            category={category}
            cost={cost}
            currencyFormat={currencyFormat}
            currencySymbol={currencySymbol}
            description={description}
            displayPrice={displayPrice}
            donationAmount={donationAmount}
            feeAndTax={feeAndTax}
            graphic={ticketGrahic}
            hideSaleDates={hideSaleDates}
            hideSelect={hideSelect}
            id={id}
            includeFee={includeFee}
            includeTax={includeTax}
            isBOGOTicket={isBOGOTicket}
            isCurrencySuffix={isCurrencySuffix}
            isFormSubmitting={isFormSubmitting}
            isTicketTiered={isTicketTiered}
            name={name}
            onDonationChange={handleDonationChange}
            onJoinWaitlist={onJoinWaitlist}
            onQuantityChange={handleQuantityChange}
            paymentConstraints={paymentConstraints}
            primaryPrice={primaryPrice}
            quantityRemaining={quantityRemaining}
            salesEnd={salesEnd}
            salesStart={salesStart}
            selectedQuantity={selectedQuantity}
            shouldHideStatus={shouldHideStatus}
            showDiscountedPill={showDiscountedPill}
            showJoinWaitlist={showJoinWaitlist}
            showUnlockedPill={showUnlockedPill}
            statusKey={statusKey}
            ticketOptionsRange={ticketOptionsRange}
            ticketReseller={ticketReseller}
            urgencySignals={urgencySignals}
            variantId={variantId}
        />
    );
};

export default TicketDisplayCardContent;
